import * as React from "react";
import styles from "./DeleteOfficeWindowRequests.module.scss";
import { useToasts } from "react-toast-notifications";
import {
    AvailabilityStatus,
    ClinicianOfficeWindowRequests,
    DateFormats,
    IOfficeWindow,
    OfficeRequestStatus,
    RecurrenceRule,
    SignatureData,
    TOffices,
} from "intake_shared/src/types";
import { AclContext } from "../../../../contexts/AclContext";
import store from "../../../../store";
import { TableHeader } from "../../../../types";
import moment from "moment";
import {
    addSpaceToString,
    findAvailableSlotsThatMatchOfficeWindow,
    getStringFromScheduleDate,
    sortStringsWithNumberEndings,
} from "../../../../utils/utils";
import { uniq } from "lodash";
import { Badge } from "../../bits/Badge/Badge";
import { OFFICE_REQUEST_STATUS_COLORS } from "../../../../constants";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import { Button } from "../../bits/Button/Button";
import ChevronDownIcon from "../../icons/ChevronDown";
import Loader from "../../bits/Loader/Loader";
import TablePagination from "../../bits/Table/TablePagination";
import { SearchInput } from "../../bits/SearchInput/SearchInput";
import FilterButton from "../../bits/FilterButton/FilterButton";
import RefreshIcon from "../../icons/Refresh";
import Table from "../../bits/Table/Table";
import { fetchCliniciansByIds } from "../../../../store/clinician";
import { RejectRequestDialog } from "../RejectRequestDialog/RejectRequestDialog";
import EmptyListState from "../../bits/EmptyListState/EmptyListState";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { CacheContext } from "../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../utils/intakeEmailSignature";

export const DAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

interface DeleteOfficeWindowRequestsProps {
    setOfficeDeleteRequestCount: (value: React.SetStateAction<number>) => void;
    officeManagerLocations: string[] | undefined;
    filteredOffices: TOffices;
}

export default function DeleteOfficeWindowRequests({
    setOfficeDeleteRequestCount,
    officeManagerLocations,
    filteredOffices,
}: DeleteOfficeWindowRequestsProps) {
    const { addToast } = useToasts();
    const olc = React.useContext(CacheContext);
    const intakeSignature = getIntakeEmailSignature(olc);
    const [actionRow, setActionRow] =
        React.useState<ClinicianOfficeWindowRequests | null>(null);
    const [deleteOfficeWindowRequests, setDeleteOfficeWindowRequests] =
        React.useState<ClinicianOfficeWindowRequests[]>();
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const [refreshKey, setRefreshKey] = React.useState(0);
    const [selectedStatus, setSelectedStatus] = React.useState<
        OfficeRequestStatus | string
    >(OfficeRequestStatus.PENDING);
    const [showRejectPopUp, setShowRejectPopUp] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const pageLimit = 50;
    const [loading, setLoading] = React.useState(false);
    const [nextButtonDisabled, setNextButtonDisabled] = React.useState(false);
    const [clinicianToFind, setClinicianToFind] = React.useState("");
    const [selectedLocation, setSelectedLocation] = React.useState("");
    const [selectedDay, setSelectedDay] = React.useState("");
    const [selectedOfficeNumber, setSelectedOfficeNumber] = React.useState("");

    const [officeRequestDetails, setOfficeRequestDetails] = React.useState({
        clinician_name: "",
        start_date: new Date(),
        end_date: new Date(),
        location: "",
        office: "",
        id: "",
        requester: "",
        category: "",
    });

    const getClinicianOfficeRequests = async () => {
        const filters = {
            location: selectedLocation,
            clinician_name: clinicianToFind.toLowerCase(),
            office: selectedOfficeNumber,
            status: selectedStatus,
            day: selectedDay,
        };
        setLoading(true);

        try {
            const requests = await store.findOfficeWindowRequestsByCategory(
                loggedInUserEmail as string,
                "delete_office_window_list",
                currentPage,
                pageLimit,
                filters
            );
            setOfficeDeleteRequestCount(
                requests?.filter(
                    (request) => request.status === OfficeRequestStatus.PENDING
                ).length
            );
            setDeleteOfficeWindowRequests(requests);
            setLoading(false);
            setNextButtonDisabled(requests?.length < pageLimit);
        } catch (error) {
            setLoading(false);

            addToast(
                "Your current role does not include office management responsibilities.",
                {
                    appearance: "error",
                    autoDismiss: false,
                }
            );
        }
    };

    React.useEffect(() => {
        getClinicianOfficeRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        refreshKey,
        selectedLocation,
        selectedOfficeNumber,
        clinicianToFind,
        selectedDay,
        selectedStatus,
        currentPage,
    ]);

    const findAvailableOfficeWindowToDelete = async (
        schedule: ClinicianOfficeWindowRequests
    ) => {
        const response = await fetchCliniciansByIds([schedule.requester || ""]);
        const location = schedule.location || "";
        const office = schedule.office || "";
        const startTime = schedule.start_date || "";
        const endTime = schedule.end_date || "";
        return findAvailableSlotsThatMatchOfficeWindow(
            response[0]?.available,
            location,
            office,
            startTime,
            endTime
        );
    };

    const handleApprove = async (row: ClinicianOfficeWindowRequests) => {
        setLoading(true);
        await findAvailableOfficeWindowToDelete(
            row as ClinicianOfficeWindowRequests
        );

        const selectedClinician = await fetchCliniciansByIds([
            row.requester as string,
        ]);
        const updatedOfficeWindows: IOfficeWindow[] =
            selectedClinician?.[0]?.office_windows.filter(
                (w) => w.id !== row.office_window_request_id
            ) || [];
        const clinicianName = `${selectedClinician?.[0]?.firstname} ${selectedClinician?.[0]?.lastname}`;
        const scheduleToDelete = {
            ...row,
            id: row.office_window_request_id,
            end: row.ending_date,
            start: row.start_date,
            recurrenceRule: row.recurrence as RecurrenceRule,
            clinician_id: row.requester as string,
            ending_date: row.ending_date as string,
        };
        await store.updateClinicianOfficeWindows(
            row.requester || "",
            updatedOfficeWindows,
            "office_manager",
            scheduleToDelete,
            clinicianName,
            "",
            "delete_office_window_list",
            intakeSignature
        );

        const filteredSchedule = selectedClinician?.[0]?.available?.filter(
            (item) => {
                return findAvailableSlotsThatMatchOfficeWindow(
                    selectedClinician?.[0]?.available,
                    row.location || "",
                    row.office || "",
                    row.start_date || "",
                    row.end_date || ""
                ).every((scheduleItem) => {
                    return item.id !== scheduleItem.id;
                });
            }
        );
        await store.updateClinicianAvailability(
            row.requester || "",
            AvailabilityStatus.AVAILABLE,
            filteredSchedule
        );
        const bookingData = {
            id: row.office_window_request_id,
            clinicianId: row.requester,
            office: row.office,
            location: row.location,
            start: getStringFromScheduleDate(row.start_date),
            end: getStringFromScheduleDate(row.end_date),
            ending_date:
                row.ending_date && getStringFromScheduleDate(row.ending_date),
            status: OfficeRequestStatus.APPROVED,
            assigned_to: row.clinician_name as string,
            available: false,
            office_window_request_id: row.office_window_request_id as string,
            office_manager: loggedInUserEmail as string,
            category: row.category,
            reason: row.reason_for_delete,
            intakeSignature: intakeSignature || ({} as SignatureData),
        };

        await store.approveClinicianOfficeWindow(bookingData);
        setRefreshKey((prevKey) => prevKey + 1);
        setLoading(false);
    };

    const ActionsCell: React.FC<{ row: ClinicianOfficeWindowRequests }> = ({
        row,
    }) => {
        return (
            <OverlayTrigger
                trigger="click"
                placement="bottom-end"
                flip
                rootClose
                show={actionRow === row}
                onToggle={(show) => setActionRow(show ? row : null)}
                overlay={
                    <Popover
                        id="office-deletion-request-row-actions"
                        className={styles.actionsPopover}
                        aria-label="Row actions"
                    >
                        <Popover.Content
                            className={styles.actionsDropdownContent}
                            onClick={() => setActionRow(null)}
                        >
                            {row.status !== OfficeRequestStatus.APPROVED && (
                                <Button
                                    variant="normal"
                                    type="button"
                                    onClick={async () => handleApprove(row)}
                                >
                                    Approve
                                </Button>
                            )}

                            {row.status !== OfficeRequestStatus.REJECTED && (
                                <Button
                                    variant="normal"
                                    type="button"
                                    onClick={() => {
                                        setOfficeRequestDetails({
                                            ...officeRequestDetails,
                                            clinician_name:
                                                row.clinician_name as string,
                                            start_date: row.start_date as Date,
                                            end_date: row.end_date as Date,
                                            location: row.location || "",
                                            office: row.office || "",
                                            id: row.office_window_request_id || "",
                                            requester: row.requester || "",
                                            category: row.category || "",
                                        });
                                        setShowRejectPopUp(true);
                                    }}
                                >
                                    Reject
                                </Button>
                            )}
                        </Popover.Content>
                    </Popover>
                }
            >
                <Button
                    aria-expanded={actionRow === row}
                    iconRight={<ChevronDownIcon stroke="var(--color-white)" />}
                >
                    Actions
                </Button>
            </OverlayTrigger>
        );
    };

    const columns = React.useMemo(() => {
        const headers: TableHeader[] = [
            {
                Header: "CLINICIAN",
                id: "clinician_name",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{row.clinician_name || ""}</p>
                ),
            },
            {
                Header: "REQUEST DATE",
                id: "",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.request_date).format(
                        DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                    )}`}</p>
                ),
            },
            {
                Header: "REQUESTED DAY",
                id: "",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.start_date).format(
                        DateFormats.DAY
                    )}`}</p>
                ),
            },
            {
                Header: "REQUESTED TIME",
                id: "start_date",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.start_date).format(
                        DateFormats.HOUR_MIN_FORMAT
                    )} - ${moment(row.end_date).format(
                        DateFormats.HOUR_MIN_FORMAT
                    )}`}</p>
                ),
            },
            {
                Header: "LOCATION",
                id: "location",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row.location || ""}</p>
                ),
            },
            {
                Header: "OFFICE NO",
                id: "office_number",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row.office || ""}</p>
                ),
            },
            {
                Header: "COMMENTS",
                id: "comments",
                accessor: (row: ClinicianOfficeWindowRequests) => {
                    const comments: Array<{
                        role: string;
                        type: string;
                        comment: string;
                    }> = [];

                    // Add all available comments with their roles
                    if (row.rejection_reason) {
                        comments.push({
                            role: "Office Manager",
                            type: "Reason for Rejection",
                            comment: row.rejection_reason,
                        });
                    }

                    if (row.reason_for_delete) {
                        comments.push({
                            role: "Clinician",
                            type: "Reason for Deletion",
                            comment: row.reason_for_delete,
                        });
                    }

                    // Display all comments
                    return (
                        <div>
                            {comments && comments.length > 0 ? (
                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                        <Tooltip id="comments-tooltip">
                                            {comments.map((item, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "4px",
                                                    }}
                                                >
                                                    <div>
                                                        {item?.role}
                                                        {item?.type
                                                            ? `: ${item?.type}`
                                                            : ""}
                                                    </div>
                                                    <div>{item?.comment}</div>
                                                </div>
                                            ))}
                                        </Tooltip>
                                    }
                                >
                                    <span>
                                        {comments[0]?.role}:{" "}
                                        {comments[0]?.comment?.length > 4
                                            ? comments[0]?.comment?.substring(0, 4) +
                                              "...."
                                            : comments[0]?.comment}
                                    </span>
                                </OverlayTrigger>
                            ) : (
                                "--"
                            )}
                        </div>
                    );
                },
            },
            {
                Header: "STATUS",
                id: "status",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <Badge
                        bgColor={OFFICE_REQUEST_STATUS_COLORS[row.status || ""]}
                        content={addSpaceToString(row.status as string) || ""}
                        textColor="var(--color-white)"
                    />
                ),
            },
            {
                Header: "Actions",
                id: "actions",
                accessor: (row: ClinicianOfficeWindowRequests) => {
                    return <ActionsCell row={row} />;
                },
            },
        ];
        return headers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionRow]);

    const handleResetFilters = () => {
        setSelectedLocation("");
        setSelectedOfficeNumber("");
        setSelectedStatus("");
        setClinicianToFind("");
        setCurrentPage(1);
    };

    const handleFirstPageClick = () => {
        setCurrentPage(1);
    };

    const handlePrevClick = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextClick = () => {
        setCurrentPage(currentPage + 1);
    };

    const tableFooter = (
        <TablePagination
            currentPage={currentPage}
            onFirstPage={handleFirstPageClick}
            onPrevPage={handlePrevClick}
            onNextPage={handleNextClick}
            isNextDisabled={nextButtonDisabled}
            showPrevButton={currentPage > 1}
        />
    );

    const getLocationLabel = (location: string) => {
        return location || "Select location";
    };

    const getOfficeLabel = (office: string) => {
        return office || "Select office";
    };

    const getStatusLabel = (status: string) => {
        return addSpaceToString(status) || "Select status";
    };

    const getDayLabel = (day: string) => {
        return day || "Select day";
    };

    return (
        <div>
            <Loader show={loading} />
            {showRejectPopUp && (
                <RejectRequestDialog
                    rejectRequest={showRejectPopUp}
                    officeRequestToReject={officeRequestDetails}
                    setRejectRequest={setShowRejectPopUp}
                    setRefreshKey={setRefreshKey}
                    requestToRejectId={officeRequestDetails.id}
                    loggedInUserEmail={loggedInUserEmail as string}
                    viewFrom="delete_office_window_request"
                />
            )}

            <div className="office-manager-filters">
                <SearchInput
                    placeholder="Clinician Name"
                    value={clinicianToFind}
                    onChange={(e) => setClinicianToFind(e.target.value)}
                />
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getStatusLabel(selectedStatus)} />
                    </Dropdown.Trigger>
                    <Dropdown.Content width="190px">
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={
                                selectedStatus === ("" as OfficeRequestStatus)
                            }
                            onClick={() =>
                                setSelectedStatus("" as OfficeRequestStatus)
                            }
                        >
                            Show all
                        </Dropdown.Item>
                        {Object.values(OfficeRequestStatus)
                            .filter(
                                (s) =>
                                    s === OfficeRequestStatus.APPROVED ||
                                    s === OfficeRequestStatus.REJECTED ||
                                    s === OfficeRequestStatus.PENDING
                            )
                            .map((s) => (
                                <Dropdown.Item
                                    key={`status-${s}`}
                                    closeMenuOnSelect
                                    isSelected={selectedStatus === s}
                                    onClick={() => setSelectedStatus(s)}
                                >
                                    {addSpaceToString(s)}
                                </Dropdown.Item>
                            ))}
                    </Dropdown.Content>
                </Dropdown>

                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getDayLabel(selectedDay)} />
                    </Dropdown.Trigger>
                    <Dropdown.Content width="190px">
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedDay === ("" as string)}
                            onClick={() => setSelectedDay("" as string)}
                        >
                            Show all
                        </Dropdown.Item>
                        {Object.values(DAYS).map((day) => (
                            <Dropdown.Item
                                key={day}
                                closeMenuOnSelect
                                isSelected={selectedDay === day}
                                onClick={() => setSelectedDay(day)}
                            >
                                {day}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getLocationLabel(selectedLocation)} />
                    </Dropdown.Trigger>

                    <Dropdown.Content width="170px">
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedLocation === ""}
                            onClick={() => setSelectedLocation("")}
                        >
                            Show all
                        </Dropdown.Item>
                        {officeManagerLocations?.map((locations) => (
                            <Dropdown.Item
                                key={locations}
                                closeMenuOnSelect
                                isSelected={selectedLocation === locations}
                                onClick={() => setSelectedLocation(locations)}
                            >
                                {locations}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getOfficeLabel(selectedOfficeNumber)} />
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedOfficeNumber === ""}
                            onClick={() => setSelectedOfficeNumber("")}
                        >
                            Show all
                        </Dropdown.Item>
                        {sortStringsWithNumberEndings(
                            uniq(filteredOffices[selectedLocation] || [])
                        ).map((r) => (
                            <Dropdown.Item
                                key={r}
                                onClick={() => setSelectedOfficeNumber(r)}
                                isSelected={selectedOfficeNumber === r}
                            >
                                {r}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                <Button onClick={() => setRefreshKey((prevKey) => prevKey + 1)}>
                    <RefreshIcon />
                </Button>
                <Button
                    variant="normal"
                    className="reset-filters-button"
                    onClick={handleResetFilters}
                >
                    Reset Filters
                </Button>
            </div>
            {deleteOfficeWindowRequests &&
                (deleteOfficeWindowRequests.length === 0 ? (
                    <EmptyListState
                        emptyMessage="No deleted office window requests"
                        stateHelperText="Deleted office window requests will be shown here"
                    />
                ) : (
                    <Table
                        stickyLastColumn
                        columns={columns}
                        data={deleteOfficeWindowRequests}
                        allowSelect={false}
                        footer={tableFooter}
                    />
                ))}
        </div>
    );
}
